import { observer } from "mobx-react";
import { IApplicationFilterComponent } from "../BaseFilterComponents/IApplicationFilterComponent";
import React, { useEffect, useMemo } from "react";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { Stack, Typography } from "@mui/material";
import AcxToggleButton from "components/UI/AcxToggleButton";
import FilterSectionContainer from "components/Conversations/Views/Drawer/FilterSectionContainer";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { customFilterSelectStyles } from "components/Conversations/Views/Drawer/shared";
import {
    SafetyEventAck,
    type SafetyEvent,
    SafetyEventId,
    SafetyEventType,
    SafetyEventTypeOperator,
    resolveSafetyEventTypeOperator,
} from "stores/ApplicationFilters/Filters/SafetyEvent";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";

const safetyEventIdPrefix = "application-filters-safety-event";

const acknowledgementOptions = [
    {
        id: `${safetyEventIdPrefix}-acknowledged`,
        label: "Acknowledged",
        value: SafetyEventAck.Acknowledged,
    },
    {
        id: `${safetyEventIdPrefix}-not-acknowledged`,
        label: "Not Acknowledged",
        value: SafetyEventAck.NotAcknowledged,
    },
];

const identificationItems = [
    {
        id: `${safetyEventIdPrefix}-identified`,
        element: "Identified",
        value: SafetyEventId.Identified,
    },
    {
        id: `${safetyEventIdPrefix}-not-identified`,
        element: "Not Identified",
        value: SafetyEventId.NotIdentified,
    },
];

const typeOptions = [
    {
        id: `${safetyEventIdPrefix}-event-type-adverse-event`,
        label: "Adverse Event",
        value: SafetyEventType.AdverseEvent,
    },
    {
        id: `${safetyEventIdPrefix}-event-type-not-adverse-event`,
        label: "Not Adverse Event",
        value: SafetyEventType.NotAdverseEvent,
    },
    {
        id: `${safetyEventIdPrefix}-event-type-product-quality-complaint`,
        label: "Product Quality Complaint",
        value: SafetyEventType.ProductQualityComplaint,
    },
    {
        id: `${safetyEventIdPrefix}-event-type-not-product-quality-complaint`,
        label: "Not Product Quality Complaint",
        value: SafetyEventType.NotProductQualityComplaint,
    },
    {
        id: `${safetyEventIdPrefix}-event-type-special-situation`,
        label: "Special Situation",
        value: SafetyEventType.SpecialSituation,
    },
    {
        id: `${safetyEventIdPrefix}-event-type-not-special-situation`,
        label: "Not Special Situation",
        value: SafetyEventType.NotSpecialSituation,
    },
];

const typeOperatorOptions = [
    { label: "Is Any", value: SafetyEventTypeOperator.Or },
    { label: "Is All", value: SafetyEventTypeOperator.And },
];

function isSafetyEventType(value: unknown): value is SafetyEventType {
    return (
        value === SafetyEventType.AdverseEvent ||
        value === SafetyEventType.NotAdverseEvent ||
        value === SafetyEventType.ProductQualityComplaint ||
        value === SafetyEventType.NotProductQualityComplaint ||
        value === SafetyEventType.SpecialSituation ||
        value === SafetyEventType.NotSpecialSituation
    );
}

function isSafetyEventAcknowledgment(value: unknown): value is SafetyEventAck {
    return (
        value === SafetyEventAck.Acknowledged ||
        value === SafetyEventAck.NotAcknowledged
    );
}

function normalizeSafetyEventRestriction(value: unknown): SafetyEvent | undefined {
    if (!value || typeof value !== "object") return undefined;

    const identification = (value as SafetyEvent).identification ?? null;
    if (
        identification !== SafetyEventId.Identified &&
        identification !== SafetyEventId.NotIdentified &&
        identification !== null
    ) {
        return undefined;
    }

    return {
        identification,
        typeOperator: resolveSafetyEventTypeOperator(value as SafetyEvent),
        types: Array.isArray((value as SafetyEvent).types)
            ? (value as SafetyEvent).types.filter(isSafetyEventType)
            : [],
        acknowledgments: Array.isArray((value as SafetyEvent).acknowledgments)
            ? (value as SafetyEvent).acknowledgments.filter(
                  isSafetyEventAcknowledgment,
              )
            : [],
    };
}

function areValuesEqual<T>(left: T[], right: T[]): boolean {
    return (
        left.length === right.length &&
        left.every((value, index) => value === right[index])
    );
}

export const SafetyEventFilter = observer(
    (props: IApplicationFilterComponent) => {
        const authStore = useStore(AuthStore);
        const applicationFiltersStore = useStore(ApplicationFiltersStore);
        const safetyEventTypes = applicationFiltersStore.safetyEventTypes;
        const safetyEvent = props.store.safetyEvent;
        const restrictedSafetyEvent = useMemo(() => {
            const restrictionValues =
                props.optionRestriction?.type === "allowOnly"
                    ? props.optionRestriction.values
                    : props.restrictedValues;

            return normalizeSafetyEventRestriction(restrictionValues?.[0]);
        }, [props.optionRestriction, props.restrictedValues]);
        const restrictedTypeValues = useMemo(
            () =>
                new Set(
                    (restrictedSafetyEvent?.types ?? []).map((value) => `${value}`),
                ),
            [restrictedSafetyEvent?.types],
        );
        const restrictedAcknowledgmentValues = useMemo(
            () =>
                new Set(
                    (restrictedSafetyEvent?.acknowledgments ?? []).map(
                        (value) => `${value}`,
                    ),
                ),
            [restrictedSafetyEvent?.acknowledgments],
        );
        const identificationToggleItems = useMemo(
            () =>
                identificationItems.map((item) => ({
                    ...item,
                    disabled:
                        restrictedSafetyEvent !== undefined &&
                        restrictedSafetyEvent.identification !== null &&
                        item.value !== restrictedSafetyEvent.identification,
                })),
            [restrictedSafetyEvent],
        );
        const isTypeLockedBySource =
            restrictedSafetyEvent?.typeOperator === SafetyEventTypeOperator.And &&
            restrictedSafetyEvent.types.length > 0;

        const adjustedTypeOptions = useMemo(() => {
            return typeOptions
                .filter((option) => safetyEventTypes.includes(option.value))
                .filter((option) => {
                    if (restrictedTypeValues.size === 0) return true;
                    return restrictedTypeValues.has(`${option.value}`);
                });
        }, [restrictedTypeValues, safetyEventTypes]);
        const adjustedAcknowledgmentOptions = useMemo(() => {
            return acknowledgementOptions.filter((option) => {
                if (restrictedAcknowledgmentValues.size === 0) return true;
                return restrictedAcknowledgmentValues.has(`${option.value}`);
            });
        }, [restrictedAcknowledgmentValues]);

        useEffect(() => {
            if (!restrictedSafetyEvent) return;

            const currentTypes = safetyEvent?.types ?? [];
            const currentAcknowledgments = safetyEvent?.acknowledgments ?? [];
            const currentTypeOperator =
                safetyEvent
                    ? resolveSafetyEventTypeOperator(safetyEvent)
                    : SafetyEventTypeOperator.Or;
            const nextIdentification =
                restrictedSafetyEvent.identification ??
                safetyEvent?.identification ??
                null;
            const nextTypes =
                isTypeLockedBySource
                    ? restrictedSafetyEvent.types
                    : restrictedSafetyEvent.types.length > 0
                    ? currentTypes.filter((value) =>
                          restrictedTypeValues.has(`${value}`),
                      ).length > 0
                        ? currentTypes.filter((value) =>
                              restrictedTypeValues.has(`${value}`),
                          )
                        : restrictedSafetyEvent.types
                    : currentTypes;
            const nextAcknowledgments =
                restrictedSafetyEvent.acknowledgments.length > 0
                    ? currentAcknowledgments.filter((value) =>
                          restrictedAcknowledgmentValues.has(`${value}`),
                      ).length > 0
                        ? currentAcknowledgments.filter((value) =>
                              restrictedAcknowledgmentValues.has(`${value}`),
                          )
                        : restrictedSafetyEvent.acknowledgments
                    : currentAcknowledgments;
            const nextTypeOperator = isTypeLockedBySource
                ? restrictedSafetyEvent.typeOperator
                : currentTypeOperator;
            const shouldSync =
                safetyEvent?.identification !== nextIdentification ||
                currentTypeOperator !== nextTypeOperator ||
                !areValuesEqual(currentTypes, nextTypes) ||
                !areValuesEqual(currentAcknowledgments, nextAcknowledgments);

            if (!shouldSync) return;

            props.store.setSafetyEvent({
                identification: nextIdentification,
                typeOperator: nextTypeOperator,
                types: nextTypes,
                acknowledgments: nextAcknowledgments,
            });
        }, [
            props.store,
            restrictedAcknowledgmentValues,
            restrictedSafetyEvent,
            restrictedTypeValues,
            safetyEvent,
            safetyEvent?.acknowledgments,
            safetyEvent?.identification,
            safetyEvent?.typeOperator,
            safetyEvent?.types,
            isTypeLockedBySource,
        ]);

        if (!authStore.canUserView("Safety Events Model")) return null;

        return (
            <Stack>
                <AcxToggleButton
                    id="application-filters-adverse-event"
                    exclusive
                    items={identificationToggleItems}
                    onChange={(value) => {
                        props.store.setSafetyEvent({
                            identification: value,
                        });
                    }}
                    defaultValue={safetyEvent?.identification}
                    justifyContent="center"
                    groupFlexGrow={1}
                    sx={{ mb: 1, maxWidth: 360 }}
                />
                <Stack>
                    <Typography sx={{ mb: 0.5 }}>
                        Safety Event Type
                    </Typography>
                    <FilterSectionContainer sx={{ mb: 1 }}>
                        <Stack>
                            <AcxSelectSingle
                                id={`${safetyEventIdPrefix}-type-operator`}
                                options={typeOperatorOptions}
                                labelField="label"
                                valueField="value"
                                defaultValue={typeOperatorOptions.find(
                                    (i) =>
                                        i.value ===
                                        (safetyEvent
                                            ? resolveSafetyEventTypeOperator(safetyEvent)
                                            : SafetyEventTypeOperator.Or),
                                )}
                                customStyle={customFilterSelectStyles}
                                isDisabled={isTypeLockedBySource}
                                onChange={(item) => {
                                    props.store.setSafetyEvent({
                                        typeOperator: item.value,
                                    });
                                }}
                            />
                            <AcxSelectMulti
                                id={`${safetyEventIdPrefix}-type`}
                                fullWidth
                                options={adjustedTypeOptions}
                                defaultValue={adjustedTypeOptions.filter(
                                    (se) =>
                                        safetyEvent?.types.includes(
                                            se.value,
                                        ),
                                )}
                                onChange={(options) => {
                                    props.store.setSafetyEvent({
                                        types:
                                            options?.map((i) => i.value) ||
                                            [],
                                    });
                                }}
                                valueField="value"
                                labelField="label"
                                containerHeight="auto"
                                maxContainerHeight="96px"
                                alignControls="flex-start"
                                isDisabled={isTypeLockedBySource}
                                isClearable={true}
                            />
                        </Stack>
                    </FilterSectionContainer>
                    <Typography sx={{ mb: 0.5 }}>
                        Safety Event Acknowledgement
                    </Typography>
                    <AcxSelectMulti
                        id={`${safetyEventIdPrefix}-acknowledgement`}
                        fullWidth
                        options={adjustedAcknowledgmentOptions}
                        defaultValue={adjustedAcknowledgmentOptions.filter(
                            (se) =>
                                safetyEvent?.acknowledgments.includes(
                                    se.value,
                                ),
                        )}
                        onChange={(options) => {
                            props.store.setSafetyEvent({
                                acknowledgments:
                                    options?.map((i) => i.value) || [],
                            });
                        }}
                        valueField="value"
                        labelField="label"
                        containerHeight="auto"
                        maxContainerHeight="96px"
                        alignControls="flex-start"
                        isClearable={true}
                    />
                </Stack>
            </Stack>
        );
    },
);
